import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, MenuItem, Select, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import axiosInstance from '../../../helpers/axios';
import { useInstituteOptions } from '../hooks/useInstituteOptions';
import { fibeTokens } from '../../../theme';

const GRADE_OPTIONS = [
    { value: 'non_key_account', label: 'Non-Key Account' },
    { value: 'key_account', label: 'Key Account (Grade A)' },
];

const MENU_PROPS = {
    PaperProps: {
        sx: {
            '& .MuiMenuItem-root': {
                fontFamily: fibeTokens.font.family,
                fontSize: 12.5,
                fontWeight: 400,
                minHeight: 'auto',
                padding: '8px 12px',
            },
        },
    },
};

function InfoGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ flex: 'none', marginTop: 1 }}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 16v-5M12 8h.01" />
        </svg>
    );
}

function InfoBanner({ children }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px 14px',
                background: fibeTokens.color.infoTint,
                border: `1px solid ${fibeTokens.color.infoBorder}`,
                borderRadius: '10px',
                color: fibeTokens.color.info,
            }}
        >
            <InfoGlyph />
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12.5, lineHeight: 1.5, color: fibeTokens.color.info }}>
                {children}
            </Box>
        </Box>
    );
}

function SlabRow({ label, value }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontFamily: fibeTokens.font.family,
                fontWeight: 400,
                fontSize: 12.5,
                color: fibeTokens.color.textMuted,
                padding: '9px 12px',
                background: fibeTokens.dialog.footerBg,
                border: `1px solid ${fibeTokens.dialog.footerBorder}`,
                borderRadius: '9px',
            }}
        >
            <span>{label}</span>
            <Box component="span" sx={{ color: fibeTokens.color.textPrimary, fontWeight: 600 }}>{value}</Box>
        </Box>
    );
}

function SlabCard({ title, badgeColor, badgeLabel, rows }) {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' }}>
                <Box component="h3" sx={{ margin: 0, fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13.5, color: fibeTokens.color.textPrimary }}>
                    {title}
                </Box>
                <Chip variant="fibePill" color={badgeColor} label={badgeLabel} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {rows.map((row) => (
                    <SlabRow key={row.label} label={row.label} value={row.value} />
                ))}
            </Box>
        </Box>
    );
}

function InstituteGradeSkeleton() {
    const columns = [{ width: 140 }, { width: 100 }, { width: 90, pill: true }, { width: 40 }, { width: 190, control: true }];
    return (
        <>
            {Array.from({ length: 4 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 18px',
                        borderBottom: rowIndex < 3 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {columns.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill || col.control ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : col.control ? 30 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : col.control ? '7px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </>
    );
}

function InstituteGradeEmptyState() {
    return (
        <Box sx={{ textAlign: 'center', padding: '40px 18px' }}>
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <InfoGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No Fibe-onboarded institutes found.
            </Box>
        </Box>
    );
}

function InstituteGradeRow({ institute }) {
    const [grade, setGrade] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [justSaved, setJustSaved] = useState(false);

    useEffect(() => {
        axiosInstance
            .get(`/fibe-billing/institutes/${institute.id}/grade`)
            .then((response) => setGrade(response.data.data.grade));
    }, [institute.id]);

    const handleChange = (newGrade) => {
        setIsSaving(true);
        setSaveError(null);
        setJustSaved(false);
        axiosInstance
            .put(`/fibe-billing/institutes/${institute.id}/grade`, { grade: newGrade })
            .then(() => {
                setGrade(newGrade);
                setJustSaved(true);
                setTimeout(() => setJustSaved(false), 2000);
            })
            .catch((error) => setSaveError(error.response?.data?.message ?? 'Could not update grade.'))
            .finally(() => setIsSaving(false));
    };

    const isKeyAccount = grade === 'key_account';

    return (
        <TableRow>
            <TableCell size="fibe" variant="body" align="left">
                {institute.brandName}
            </TableCell>
            <TableCell size="fibe" variant="body" align="left" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: fibeTokens.color.textSecondary }}>
                {institute.fibeMid}
            </TableCell>
            <TableCell size="fibe" variant="body" align="left">
                <Chip variant="fibePill" color={isKeyAccount ? 'secondary' : 'info'} label={isKeyAccount ? 'Key Account' : 'Non-Key'} />
            </TableCell>
            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {isKeyAccount ? '20%' : '23%'}
            </TableCell>
            <TableCell size="fibe" variant="body" align="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <Select
                        size="fibeSm"
                        displayEmpty
                        value={grade ?? ''}
                        disabled={grade == null || isSaving}
                        onChange={(e) => handleChange(e.target.value)}
                        MenuProps={MENU_PROPS}
                        sx={{ width: 190 }}
                    >
                        {GRADE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                    {isSaving && (
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 500, fontSize: 10.5, color: fibeTokens.color.textDisabled }}>
                            Saving...
                        </Box>
                    )}
                    {justSaved && (
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 10.5, color: fibeTokens.color.success }}>
                            Saved
                        </Box>
                    )}
                    {saveError && (
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 500, fontSize: 10.5, color: fibeTokens.color.danger }}>
                            {saveError}
                        </Box>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
}

function InstituteGradeCard({ institutes, isLoading }) {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                overflow: 'hidden',
                marginBottom: '14px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                    Institute Grade Management
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 11.5, color: fibeTokens.color.textDisabled }}>
                    Changes save immediately
                </Box>
            </Box>

            <Box sx={{ margin: '16px 18px 0' }}>
                <InfoBanner>
                    Grades determine which commission slab applies. Key Account (Grade A) has a lower base rate but
                    benefits from a 50:50 split on IRR above 20% instead of 23%.
                </InfoBanner>
            </Box>

            {isLoading ? (
                <Box sx={{ marginTop: '12px' }}>
                    <InstituteGradeSkeleton />
                </Box>
            ) : institutes.length === 0 ? (
                <InstituteGradeEmptyState />
            ) : (
                <Box sx={{ overflowX: 'auto', marginTop: '12px' }}>
                    <Table sx={{ width: '100%', minWidth: '760px', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell size="fibe" variant="head" align="left">Institute</TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 150 }}>Merchant ID</TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 130 }}>Current Grade</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 100 }}>Slab A Cap</TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 250 }}>Change Grade</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {institutes.map((institute) => (
                                <InstituteGradeRow key={institute.id} institute={institute} />
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

function TenureRuleCard() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box component="h3" sx={{ margin: '0 0 12px', fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13.5, color: fibeTokens.color.textPrimary }}>
                Tenure Rule
            </Box>
            <Box sx={{ display: 'flex', gap: '14px' }}>
                <Box
                    sx={{
                        flex: 1,
                        padding: '13px 15px',
                        border: `1px solid ${fibeTokens.dialog.icon.green.border}`,
                        background: fibeTokens.color.successTint,
                        borderRadius: '10px',
                    }}
                >
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 12.5, color: fibeTokens.color.success }}>
                        Tenure ≤ 12 months
                    </Box>
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12, lineHeight: 1.5, color: fibeTokens.color.success, marginTop: '3px' }}>
                        100% commission upfront — single milestone
                    </Box>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        padding: '13px 15px',
                        border: `1px solid ${fibeTokens.color.border}`,
                        background: fibeTokens.dialog.footerBg,
                        borderRadius: '10px',
                    }}
                >
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 12.5, color: fibeTokens.color.textSecondary }}>
                        Tenure &gt; 12 months
                    </Box>
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12, lineHeight: 1.5, color: fibeTokens.color.textMuted, marginTop: '3px' }}>
                        50 / 25 / 25 staggered payout structure
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

const NON_KEY_ROWS = [
    { label: 'Slab A (IRR ≤ 23%)', value: 'Fibe 18% · FeeMonk IRR−18%' },
    { label: 'Slab B (IRR > 23%)', value: '50:50 on excess' },
    { label: 'Cap', value: '28% IRR' },
];

const KEY_ACCOUNT_ROWS = [
    { label: 'Slab A (IRR ≤ 20%)', value: 'Fibe 16.50% · FeeMonk 3.50%' },
    { label: 'Slab B (IRR > 20%)', value: '50:50 on excess' },
    { label: 'Cap', value: '28% IRR' },
];

function SettingsTab() {
    const { institutes, isLoading } = useInstituteOptions();
    const fibeOnboardedInstitutes = useMemo(
        () => institutes.filter((institute) => institute.fibeMid && institute.fibeMid.trim() !== ''),
        [institutes],
    );

    return (
        <div>
            <Box sx={{ marginBottom: '14px' }}>
                <Box component="h1" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}>
                    Settings &amp; Configuration
                </Box>
                <Box sx={{ margin: '5px 0 0', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                    Grades and slabs
                </Box>
            </Box>

            <InstituteGradeCard institutes={fibeOnboardedInstitutes} isLoading={isLoading} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <SlabCard title="Non-Key Accounts" badgeColor="info" badgeLabel="Default" rows={NON_KEY_ROWS} />
                <SlabCard title="Key Accounts (Grade A)" badgeColor="secondary" badgeLabel="Grade A" rows={KEY_ACCOUNT_ROWS} />
            </Box>

            <TenureRuleCard />
        </div>
    );
}

export default SettingsTab;
